import { commentQueryKeyMap } from '@/features/comment/utils/query-keys';
import type { sheetTypeEnum } from '@/utils/enum';
import type { z } from 'zod';

export const queryKeyMap = {
  projects: (params?: object) =>
    ['projects', params].filter((item) => item !== undefined),
  projectSummary: (params?: object) =>
    ['projectSummary', params].filter((item) => item !== undefined),
  projectsOptions: (params?: {
    fiscalYear?: string;
    sheetType?: z.infer<typeof sheetTypeEnum>;
    skipAdministrativeReformTeamEvaluation?: boolean;
  }) => ['projectsOptions', params].filter((item) => item !== undefined),
  projectIds: (params?: object) =>
    ['projectIds', params].filter((item) => item !== undefined),
  projectGroup: (id?: string) =>
    ['projectGroup', id].filter((item) => item !== undefined),
  projectGroupSummary: (id?: string) =>
    ['projectGroupSummary', id].filter((item) => item !== undefined),
  groupProjects: (id?: string, page?: number, ordering?: string | null) =>
    [
      'groupProjects',
      id,
      page === undefined && ordering === undefined
        ? undefined
        : { page, ordering },
    ].filter((item) => item !== undefined),
  group: (id: string) => ['group', id],
  project: (id?: string) =>
    ['project', id].filter((item) => item !== undefined),
  subProjects: (projectId?: string) =>
    ['subProjects', projectId].filter((item) => item !== undefined),
  consecutiveFiscalYears: (id?: string) =>
    ['consecutiveFiscalYears', id].filter((item) => item !== undefined),
  consecutiveRequestAmounts: (id?: string) =>
    ['consecutiveRequestAmounts', id].filter((item) => item !== undefined),
  consecutiveBudgets: (id?: string) =>
    ['consecutiveBudgets', id].filter((item) => item !== undefined),
  consecutiveExecutions: (id?: string) =>
    ['consecutiveExecutions', id].filter((item) => item !== undefined),
  relatedMaterials: (projectId?: string) =>
    ['relatedMaterials', projectId].filter((item) => item !== undefined),
  activities: (projectId?: string) =>
    ['activities', projectId].filter((item) => item !== undefined),
  activityTree: (projectId?: string) =>
    ['activityTree', projectId].filter((item) => item !== undefined),
  indicators: (projectId?: object) =>
    ['indicators', projectId].filter((item) => item !== undefined),
  indicator: (indicatorId?: string) =>
    ['indicator', indicatorId].filter((item) => item !== undefined),
  consecutiveIndicators: (indicatorId?: string) =>
    ['consecutiveIndicators', indicatorId].filter((item) => item !== undefined),
  allConsecutiveIndicators: () =>
    ['allConsecutiveIndicators'].filter((item) => item !== undefined),
  nestedConsecutiveIndicators: (indicatorIds?: string[]) =>
    ['nestedConsecutiveIndicators', indicatorIds].filter(
      (item) => item !== undefined,
    ),
  fundBusiness: (projectId?: string) =>
    ['fundBusiness', projectId].filter((item) => item !== undefined),
  businessForm: (projectId?: string) =>
    ['businessForm', projectId].filter((item) => item !== undefined),
  projectOrganizations: (projectId?: string, params?: object) =>
    ['projectOrganizations', projectId, params].filter(
      (item) => item !== undefined,
    ),
  projectBusinessManagers: (projectId: string) =>
    ['businessManagers', projectId].filter(Boolean),
  projectAdditionalJurisdictionalUsers: (projectId: string) =>
    ['additionalJurisdictionalUsers', projectId].filter(Boolean),
  paymentGroups: (projectId?: string) =>
    ['paymentGroups', projectId].filter((item) => item !== undefined),
  payments: (params?: object) => ['payments', params],
  contractorPayments: (projectId?: string) =>
    ['contractorPayments', projectId].filter((item) => item !== undefined),
  paymentEdges: (projectId?: string) =>
    ['paymentEdges', projectId].filter((item) => item !== undefined),
  paymentGraphData: (projectId?: string) =>
    ['paymentGraphData', projectId].filter((item) => item !== undefined),
  paymentGroupUploaderTemplate: (projectId?: string) =>
    ['paymentGroupUploaderTemplate', projectId].filter(
      (item) => item !== undefined,
    ),
  SegmentExpenditureAndExecution: (projectId?: string) =>
    ['segmentExpenditureAndExecution', projectId].filter(
      (item) => item !== undefined,
    ),
  localGovernmentFundIndividualReport: (projectId?: string) =>
    ['localGovernmentFundIndividualReport', projectId].filter(
      (item) => item !== undefined,
    ),
};

export const invalidateListMap = {
  updateDetail: (projectId) => [
    queryKeyMap.projects(),
    queryKeyMap.projectSummary(),
    queryKeyMap.projectIds(),
    queryKeyMap.projectGroupSummary(),
    queryKeyMap.groupProjects(),
    queryKeyMap.project(projectId),
    queryKeyMap.projectOrganizations(projectId),
    queryKeyMap.projectBusinessManagers(projectId),
    commentQueryKeyMap.commentInputCandidateOrganizations(),
  ],
  updateDetailedBreakdown: (projectId) => [
    queryKeyMap.projects(),
    queryKeyMap.subProjects(),
    queryKeyMap.projectSummary(),
    queryKeyMap.projectIds(),
    queryKeyMap.projectGroupSummary(),
    queryKeyMap.project(projectId),
    queryKeyMap.consecutiveRequestAmounts(),
    queryKeyMap.consecutiveBudgets(),
    queryKeyMap.consecutiveExecutions(),
    queryKeyMap.localGovernmentFundIndividualReport(projectId),
  ],
  updateList: () => [
    queryKeyMap.projects(),
    queryKeyMap.projectSummary(),
    queryKeyMap.projectIds(),
    queryKeyMap.consecutiveFiscalYears(),
    queryKeyMap.consecutiveRequestAmounts(),
    queryKeyMap.consecutiveBudgets(),
    queryKeyMap.consecutiveExecutions(),
  ],
};
